import { Link, useLocation } from "react-router-dom";
import { Package, FolderDown, Settings, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import zitiuLogo from "@/assets/zitiu-logo-black.png";

/**
 * Grouped by what each surface is *for*, not by what it contains. Studio is
 * where visuals are made, Products are what you supply, Assets are what comes
 * out. Without the headings they all read as "places with images in them",
 * which is the confusion this grouping is meant to remove.
 */
const navGroups: { heading: string; items: NavItem[] }[] = [
  {
    heading: "Create",
    items: [
      { path: "/app/studio", label: "Studio", icon: Sparkles },
    ],
  },
  {
    heading: "Library",
    items: [
      { path: "/app/asset-library", label: "Assets", icon: FolderDown },
      { path: "/app/products", label: "Products", icon: Package },
    ],
  },
];

interface NavItem {
  path: string;
  label: string;
  icon: typeof Package;
}

const bottomItems: NavItem[] = [
  { path: "/app/settings", label: "Settings", icon: Settings },
];

export function AppSidebar() {
  const location = useLocation();

  const renderNavLink = (item: NavItem) => {
    const path = location.pathname;
    const isActive =
      path === item.path ||
      (item.path === "/app/products" && path.startsWith("/app/products"));

    return (
      <Link
        key={item.path}
        to={item.path}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
            : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
        )}
      >
        <item.icon className="w-4 h-4" />
        {item.label}
      </Link>
    );
  };

  return (
    <aside data-tour="sidebar" className="w-60 h-screen bg-sidebar border-r border-sidebar-border flex flex-col shrink-0">
      {/* Logo */}
      <div className="h-14 px-4 border-b border-sidebar-border flex items-center shrink-0">
        <Link to="/" className="flex items-center">
          <img src={zitiuLogo} alt="Zitiu" className="h-7 w-auto" />
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 pt-6 space-y-5">
        {navGroups.map((group) => (
          <div key={group.heading} className="space-y-1">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {group.heading}
            </p>
            {group.items.map(renderNavLink)}
          </div>
        ))}
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 pb-4 pt-2 border-t border-sidebar-border space-y-1">
        {bottomItems.map(renderNavLink)}
      </div>
    </aside>
  );
}
